import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Person as PersonIcon
} from '@mui/icons-material';
// ⚠️ از `http` (نمونهٔ axios سرویس api) استفاده می‌شود، نه axios خام:
// هدر Authorization، رفرش خودکار توکن روی 401 (بعد از تغییر نقش/گرنت
// tokenVersion عوض می‌شود) و مدیریت 403/OUT_OF_SHIFT فقط از این مسیر می‌آید.
import { http } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAccess } from '../hooks/useAccess';
import { UserLoginHistoryButton } from '../components/common/UserLoginHistory';
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import { ConfirmDialog } from '../components/common';
import './EntityManagement.css';
import './UserManagement.css';

// Types
interface User {
  id?: string;
  _id?: string;
  name: string;
  email: string;
  phoneNumber?: string;
  phoneVerified?: boolean;
  avatar?: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  isAdmin?: boolean;
  isBanned?: boolean;
  banReason?: string;
  emailVerified?: boolean;
  roles: { roleId: string; tenantId: string | null; roleName?: string }[];
  createdAt: string;
  updatedAt?: string;
  lastLogin?: string;
}

interface Role {
  id?: string;
  _id?: string;
  name: string;
  description?: string;
  /** expanded permission set (picks + dependencies) — used for the count badge */
  permissions?: string[];
  permissionPicks?: string[];
  isActive?: boolean;
  tenantId?: string | null;
}

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  phoneVerified: boolean;
  password: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  roles: { roleId: string; tenantId: string | null }[];
}

const UserManagement: React.FC = () => {
  // Session 3 cutover: menuStructure-based gating replaced by useAccess.has().
  const { has } = useAccess();
  const canCreate = has('admin_user_create');
  const canEdit = has('admin_user_update');
  const canDelete = has('admin_user_delete');

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'email' | 'phone' | 'role'>('name');
  const [roleFilter, setRoleFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);

  // Form
  const [form, setForm] = useState<FormData>({
    name: '', email: '', phoneNumber: '', phoneVerified: false, password: '', isActive: true, isSuperAdmin: false, roles: []
  });

  // Iranian mobile validation: ^09\d{9}$ (matches backend User schema)
  const isValidIranianPhone = (val: string): boolean => /^09\d{9}$/.test(val.trim());

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' as 'success' | 'error' });

  useEffect(() => { fetchUsers(); }, [page, limit]);
  useEffect(() => { fetchRoles(); }, []);

  const getId = (u: User | null) => u?.id || u?._id || '';
  const getRoleName = (id: string) => roles.find(r => r.id === id || r._id === id)?.name || 'نقش';
  const formatDate = (d: string | undefined) => d ? new Date(d).toLocaleDateString('fa-IR') : '-';
  const formatDateTime = (d: string | undefined) => {
    if (!d) return '-';
    const date = new Date(d);
    return `${date.toLocaleDateString('fa-IR')} - ${date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}`;
  };
  const notify = (msg: string, type: 'success' | 'error') => setSnackbar({ open: true, msg, type });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: any = { page: page + 1, limit };
      if (search.trim()) params.search = search;
      if (roleFilter) params.roleId = roleFilter;

      const res = await http.get('/admin/users', { params });

      let data: User[] = [];
      if (res.data?.data) {
        data = Array.isArray(res.data.data) ? res.data.data : res.data.data.items || [];
        data = data.map(u => ({ ...u, id: u.id || u._id }));
        setTotal(res.data.pagination?.total || data.length);
      }

      // Client-side filtering based on search type
      if (search.trim()) {
        const term = search.toLowerCase();
        if (searchType === 'name') {
          data = data.filter(u => u.name.toLowerCase().includes(term));
        } else if (searchType === 'email') {
          data = data.filter(u => (u.email || '').toLowerCase().includes(term));
        } else if (searchType === 'phone') {
          // Strip non-digits for phone matching
          const termDigits = term.replace(/\D/g, '');
          data = data.filter(u => (u.phoneNumber || '').replace(/\D/g, '').includes(termDigits));
        }
      }

      // Client-side role filter if not supported by API
      if (roleFilter && !params.roleId) {
        data = data.filter(u => u.roles?.some(r => r.roleId === roleFilter));
      }

      setUsers(data);
    } catch {
      notify('خطا در دریافت', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await http.get('/admin/roles', { params: { limit: 100 } });
      if (res.data?.data) {
        const data = Array.isArray(res.data.data) ? res.data.data : res.data.data.items || [];
        setRoles(data.map((r: any) => ({ ...r, id: r.id || r._id })));
      }
    } catch {}
  };

  const openCreate = () => {
    setDialogMode('create');
    setSelectedUser(null);
    setForm({ name: '', email: '', phoneNumber: '', phoneVerified: false, password: '', isActive: true, isSuperAdmin: false, roles: [] });
    setDialogOpen(true);
  };

  const openEdit = (user: User) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setForm({
      name: user.name,
      email: user.email,
      phoneNumber: user.phoneNumber || '',
      phoneVerified: !!user.phoneVerified,
      password: '',
      isActive: user.isActive,
      isSuperAdmin: user.isSuperAdmin,
      roles: user.roles || []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    // Validate phone format if provided
    if (form.phoneNumber.trim() && !isValidIranianPhone(form.phoneNumber.trim())) {
      notify('فرمت شماره موبایل صحیح نیست (مثال: 09121234567)', 'error');
      return;
    }
    try {
      // Normalize: empty phone → undefined so unique-sparse index works correctly
      const phoneClean = form.phoneNumber.trim();
      const payload: any = {
        name: form.name,
        email: form.email,
        password: form.password,
        isActive: form.isActive,
        isSuperAdmin: form.isSuperAdmin,
        roles: form.roles,
        ...(phoneClean ? { phoneNumber: phoneClean, phoneVerified: form.phoneVerified } : {}),
      };
      if (dialogMode === 'create') {
        await http.post('/admin/users', payload);
        notify(
          form.roles.length
            ? `ادمین ایجاد شد و ${form.roles.length} نقش به او اختصاص یافت`
            : 'ادمین ایجاد شد (بدون نقش — تا زمان اختصاص نقش فقط «کارهای من» را می‌بیند)',
          'success'
        );
      } else {
        const id = getId(selectedUser);
        const { password, roles: nextRoles, ...data } = payload;
        await http.patch(`/admin/users/${id}`, data);
        if (password && password.trim()) {
          await http.patch(`/admin/users/${id}`, { password, _method: 'password_update' });
        }
        // نقش‌ها فقط وقتی POST می‌شوند که واقعاً عوض شده باشند — هر POST
        // tokenVersion کاربر هدف را عوض می‌کند و به او اعلان می‌فرستد.
        const { added, removed } = roleDiff;
        let msg = 'بروزرسانی شد';
        if (added.length || removed.length) {
          const res = await http.post(`/admin/users/${id}/roles`, { roles: nextRoles });
          const a: string[] = res.data?.added || added.map(r => r.name);
          const r: string[] = res.data?.removed || removed.map(r => r.name);
          const parts: string[] = [];
          if (a.length) parts.push(`افزوده: ${a.join('، ')}`);
          if (r.length) parts.push(`حذف: ${r.join('، ')}`);
          msg = `نقش‌ها به‌روز شد (${parts.join(' · ')}) — به کاربر اطلاع داده شد`;
        }
        notify(msg, 'success');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e: any) {
      notify(e?.response?.data?.message || e?.message || 'خطا در ذخیره', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await http.delete(`/admin/users/${getId(deleteUser)}`);
      notify('حذف شد', 'success');
      setDeleteOpen(false);
      fetchUsers();
    } catch (e: any) {
      notify(e?.response?.data?.message || 'خطا در حذف', 'error');
    }
  };

  // ── تفاوت نقش‌های فرم با نقش‌های فعلی کاربر (برای بج «جدید/حذف» و POST مشروط) ──
  const roleById = (id: string) => roles.find(r => r.id === id || r._id === id);
  const originalRoleIds = new Set((dialogMode === 'edit' ? selectedUser?.roles || [] : []).map(r => r.roleId));
  const formRoleIds = new Set(form.roles.map(r => r.roleId));
  const roleDiff = {
    added: Array.from(formRoleIds).filter(id => !originalRoleIds.has(id)).map(id => roleById(id)).filter(Boolean) as Role[],
    removed: Array.from(originalRoleIds).filter(id => !formRoleIds.has(id)).map(id => roleById(id)).filter(Boolean) as Role[],
  };
  const toggleRole = (r: Role, on: boolean) => {
    if (on) setForm(f => ({ ...f, roles: [...f.roles.filter(fr => fr.roleId !== r.id), { roleId: r.id!, tenantId: r.tenantId || null }] }));
    else setForm(f => ({ ...f, roles: f.roles.filter(fr => fr.roleId !== r.id) }));
  };
  const permCount = (r: Role) => {
    if (r.permissions?.includes('*')) return null;
    return r.permissions?.length ?? r.permissionPicks?.length ?? 0;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="users-page">
      {/* Header */}
      <header className="users-header">
        <div>
          <h1 className="users-title">مدیریت ادمین‌ها</h1>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>مدیریت حساب‌های کاربری</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchUsers} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          {canCreate && (
            <button className="header-btn primary" onClick={openCreate}>
              <AddIcon fontSize="small" />
              <span>افزودن ادمین</span>
            </button>
          )}
        </div>
      </header>

      {/* Stats */}
      <div className="users-stats">
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon blue"><PeopleIcon /></div>
              <div>
                <p className="users-stat-label">کل ادمین‌ها</p>
                <p className="users-stat-value">{total}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon green"><ViewIcon /></div>
              <div>
                <p className="users-stat-label">فعال</p>
                <p className="users-stat-value">{users.filter(u => u.isActive).length}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon red"><DeleteIcon /></div>
              <div>
                <p className="users-stat-label">مسدود</p>
                <p className="users-stat-value">{users.filter(u => u.isBanned).length}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon orange"><PeopleIcon /></div>
              <div>
                <p className="users-stat-label">غیرفعال</p>
                <p className="users-stat-value">{users.filter(u => !u.isActive).length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap" style={{ display: 'flex', gap: 8 }}>
              <select
                className="filter-field"
                style={{ maxWidth: 110 }}
                value={searchType}
                onChange={e => setSearchType(e.target.value as any)}
              >
                <option value="name">نام</option>
                <option value="email">ایمیل</option>
                <option value="phone">موبایل</option>
              </select>
              <input
                className="filter-field"
                type="text"
                placeholder={
                  searchType === 'phone' ? '09121234567' :
                  searchType === 'email' ? 'example@mail.com' : 'نام کاربر'
                }
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchUsers()}
                dir={searchType === 'name' ? 'auto' : 'ltr'}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نقش</label>
            <select
              className="filter-field"
              value={roleFilter}
              onChange={e => { setRoleFilter(e.target.value); setPage(0); }}
            >
              <option value="">همه نقش‌ها</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={() => fetchUsers()}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {(search || roleFilter) && (
              <button className="filter-btn outline" onClick={() => { setSearch(''); setRoleFilter(''); setPage(0); fetchUsers(); }}>
                پاک
              </button>
            )}
          </div>
        </div>
        {(search || roleFilter) && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {search && (
              <span className="filter-tag">
                {searchType === 'name' ? 'نام' : searchType === 'email' ? 'ایمیل' : searchType === 'phone' ? 'موبایل' : 'جستجو'}: {search}
                <button onClick={() => { setSearch(''); fetchUsers(); }}>×</button>
              </span>
            )}
            {roleFilter && (
              <span className="filter-tag">
                نقش: {roles.find(r => r.id === roleFilter)?.name}
                <button onClick={() => { setRoleFilter(''); fetchUsers(); }}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={() => { setSearch(''); setRoleFilter(''); setPage(0); fetchUsers(); }}>
              پاک کردن همه
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="users-table-wrapper">
        {loading ? (
          <div className="users-loading"><CircularProgress size={36} /></div>
        ) : users.length === 0 ? (
          <div className="users-empty">
            <PeopleIcon className="users-empty-icon" />
            <p className="users-empty-text">کاربری یافت نشد</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="users-table">
              <thead>
                <tr>
                  <th>کاربر</th>
                  <th>موبایل</th>
                  <th>نقش‌ها</th>
                  <th>وضعیت</th>
                  <th>آخرین ورود</th>
                  <th>بروزرسانی</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={getId(user)} className={user.isBanned ? 'row-banned' : ''}>
                    <td>
                      <div className="user-info-cell">
                        <div className="user-avatar" onClick={() => { setDetailsUser(user); setDetailsOpen(true); }}>
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} />
                          ) : (
                            <PersonIcon />
                          )}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            {user.name}
                            {user.isSuperAdmin && <span className="tag tag-sm tag-admin">ارشد</span>}
                            {user.isAdmin && !user.isSuperAdmin && <span className="tag tag-sm tag-admin">ادمین</span>}
                          </div>
                          <div className="user-email">{user.email}</div>
                          {user.isBanned && (
                            <div className="user-badges">
                              {user.isBanned && <span className="tag tag-xs tag-danger">مسدود</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="cell-phone">{user.phoneNumber || '-'}</td>
                    <td>
                      <div className="roles-list">
                        {user.roles?.length > 0 ? user.roles.slice(0, 2).map((r, i) => (
                          <span key={i} className="tag tag-xs tag-role">{r.roleName || getRoleName(r.roleId)}</span>
                        )) : '-'}
                        {user.roles?.length > 2 && <span className="tag tag-xs tag-role">+{user.roles.length - 2}</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${user.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {user.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="cell-date">{formatDateTime(user.lastLogin)}</td>
                    <td className="cell-date">{formatDate(user.updatedAt)}</td>
                    <td>
                      <div className="actions-cell">
                        <button className="action-btn view" onClick={() => { setDetailsUser(user); setDetailsOpen(true); }} title="جزئیات">
                          <ViewIcon fontSize="small" />
                        </button>
                        <UserLoginHistoryButton userId={getId(user)} userName={user.name} buttonLabel="" size="small" />
                        <EntityHistoryButton entityType="User" entityId={getId(user)} buttonLabel="" size="small" />
                        {canEdit && (
                          <button className="action-btn edit" onClick={() => openEdit(user)} title="ویرایش">
                            <EditIcon fontSize="small" />
                          </button>
                        )}
                        {canDelete && (
                          <button className="action-btn delete" onClick={() => { setDeleteUser(user); setDeleteOpen(true); }} title="حذف">
                            <DeleteIcon fontSize="small" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="users-cards">
              {users.map(user => {
                const isOpen = expandedId === getId(user);
                return (
                  <div key={getId(user)} className={`user-card ${user.isBanned ? 'card-banned' : ''}`}>
                    <div className="user-card-header" onClick={() => setExpandedId(isOpen ? null : getId(user))}>
                      <div className="user-card-avatar" onClick={e => { e.stopPropagation(); setDetailsUser(user); setDetailsOpen(true); }}>
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          <PersonIcon />
                        )}
                      </div>
                      <div className="user-card-info">
                        <div className="user-card-name">
                          {user.name}
                          {user.isSuperAdmin && <span className="tag tag-xs tag-admin">ارشد</span>}
                          {user.isAdmin && !user.isSuperAdmin && <span className="tag tag-xs tag-admin">ادمین</span>}
                        </div>
                        <div className="user-card-email">{user.email}</div>
                        {user.isBanned && (
                          <div className="user-card-badges">
                            {user.isBanned && <span className="tag tag-xs tag-danger">مسدود</span>}
                          </div>
                        )}
                      </div>
                      <div className="user-card-status">
                        <span className={`tag tag-sm ${user.isActive ? 'tag-active' : 'tag-inactive'}`}>
                          {user.isActive ? 'فعال' : 'غیرفعال'}
                        </span>
                        <button className={`user-card-toggle ${isOpen ? 'open' : ''}`}>
                          <KeyboardArrowDown fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <div className={`user-card-details ${isOpen ? 'open' : ''}`}>
                      <div className="user-card-row">
                        <span className="user-card-label">موبایل</span>
                        <span className="user-card-value ltr">{user.phoneNumber || '-'}</span>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">نقش‌ها</span>
                        <div className="roles-list">
                          {user.roles?.length > 0 ? user.roles.map((r, i) => (
                            <span key={i} className="tag tag-xs tag-role">{r.roleName || getRoleName(r.roleId)}</span>
                          )) : '-'}
                        </div>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">آخرین ورود</span>
                        <span className="user-card-value ltr">{formatDateTime(user.lastLogin)}</span>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">تاریخ ایجاد</span>
                        <span className="user-card-value">{formatDate(user.createdAt)}</span>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">آخرین بروزرسانی</span>
                        <span className="user-card-value">{formatDate(user.updatedAt)}</span>
                      </div>
                      {user.isBanned && user.banReason && (
                        <div className="user-card-row">
                          <span className="user-card-label">دلیل مسدودی</span>
                          <span className="user-card-value">{user.banReason}</span>
                        </div>
                      )}
                      <div className="user-card-actions">
                        <button className="user-card-btn" onClick={() => { setDetailsUser(user); setDetailsOpen(true); }}>
                          <ViewIcon fontSize="small" /> جزئیات
                        </button>
                        <UserLoginHistoryButton userId={getId(user)} userName={user.name} buttonLabel="ورودها" size="small" variant="outlined" />
                        <EntityHistoryButton entityType="User" entityId={getId(user)} buttonLabel="تاریخچه" size="small" />
                        {canEdit && (
                          <button className="user-card-btn primary" onClick={() => openEdit(user)}>
                            <EditIcon fontSize="small" /> ویرایش
                          </button>
                        )}
                        {canDelete && (
                          <button className="user-card-btn danger" onClick={() => { setDeleteUser(user); setDeleteOpen(true); }}>
                            <DeleteIcon fontSize="small" /> حذف
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="users-pagination">
              <span className="pagination-info">{page * limit + 1} تا {Math.min((page + 1) * limit, total)} از {total}</span>
              <div className="pagination-controls">
                <select className="users-select" value={limit} onChange={e => { setLimit(+e.target.value); setPage(0); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                  <ChevronLeft fontSize="small" />
                </button>
                <button className="pagination-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                  <ChevronRight fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth className="edit-dialog">
        <div className="edit-dialog-title">
          <span>{dialogMode === 'create' ? 'ایجاد ادمین جدید' : 'ویرایش ادمین'}</span>
          <button className="details-close" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            {/* Basic Info */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>ایمیل</label>
                    <input type="email" placeholder="admin@example.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} dir="ltr" disabled={dialogMode === 'edit'} />
                  </div>
                  <div className="edit-field">
                    <label>نام کامل</label>
                    <input type="text" placeholder="نام و نام خانوادگی" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="edit-field">
                    <label>شماره موبایل (اختیاری)</label>
                    <input
                      type="tel"
                      placeholder="09121234567"
                      value={form.phoneNumber}
                      onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                      dir="ltr"
                      maxLength={11}
                    />
                  </div>
                  <div className="edit-field">
                    <label>{dialogMode === 'create' ? 'رمز عبور' : 'رمز عبور جدید (اختیاری)'}</label>
                    <input type="password" placeholder={dialogMode === 'create' ? 'حداقل ۱۲ کاراکتر' : 'برای تغییر وارد کنید'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} dir="ltr" />
                  </div>
                </div>
              </div>
            </div>

            {/* Roles */}
            <div className="edit-card">
              <div className="edit-card-header">
                نقش‌ها
                {form.roles.length > 0 && <span className="edit-card-badge">{form.roles.length} انتخاب شده</span>}
                {(roleDiff.added.length > 0 || roleDiff.removed.length > 0) && (
                  <span className="edit-card-badge edit-card-badge-diff">
                    {roleDiff.added.length > 0 && <span className="diff-add">+{roleDiff.added.length}</span>}
                    {roleDiff.removed.length > 0 && <span className="diff-del">−{roleDiff.removed.length}</span>}
                  </span>
                )}
              </div>
              <div className="edit-card-body">
                {form.isSuperAdmin && (
                  <div className="edit-roles-hint">
                    مدیر ارشد بدون توجه به نقش‌ها همهٔ دسترسی‌ها را دارد؛ نقش‌ها فقط برای زمانی است که این سوییچ خاموش شود.
                  </div>
                )}
                {roles.length === 0 ? (
                  <span className="edit-no-roles">نقشی تعریف نشده — ابتدا از «نقش‌ها» یک نقش بسازید</span>
                ) : (
                  <div className="edit-roles-grid">
                    {roles.map(r => {
                      const isSelected = formRoleIds.has(r.id!);
                      const wasSelected = originalRoleIds.has(r.id!);
                      const state = isSelected && !wasSelected ? 'added' : !isSelected && wasSelected ? 'removed' : isSelected ? 'selected' : '';
                      const count = permCount(r);
                      return (
                        <label key={r.id} className={`edit-role-card ${state}`}>
                          <input type="checkbox" checked={isSelected} onChange={e => toggleRole(r, e.target.checked)} />
                          <div className="edit-role-card-body">
                            <div className="edit-role-card-title">
                              <span className="edit-role-card-name">{r.name}</span>
                              {state === 'added' && <span className="edit-role-flag add">جدید</span>}
                              {state === 'removed' && <span className="edit-role-flag del">حذف می‌شود</span>}
                              {r.isActive === false && <span className="edit-role-flag off">غیرفعال</span>}
                            </div>
                            {r.description && <div className="edit-role-card-desc">{r.description}</div>}
                            <div className="edit-role-card-meta">
                              {count === null ? 'دسترسی کامل (*)' : `${count.toLocaleString('fa-IR')} دسترسی`}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {dialogMode === 'edit' && (roleDiff.added.length > 0 || roleDiff.removed.length > 0) && (
                  <div className="edit-roles-hint">
                    با ذخیره، نقش‌های کاربر بلافاصله اعمال می‌شود؛ خودش اعلان می‌گیرد و بدون خروج از پنل منوهایش به‌روز می‌شود.
                  </div>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات</div>
              <div className="edit-card-body">
                <div className="edit-switches">
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">وضعیت فعال</div>
                      <div className="edit-switch-desc">کاربر می‌تواند وارد سیستم شود</div>
                    </div>
                    <Switch size="small" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                  </label>
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">مدیر ارشد</div>
                      <div className="edit-switch-desc">دسترسی کامل به تمام بخش‌ها</div>
                    </div>
                    <Switch size="small" checked={form.isSuperAdmin} onChange={e => setForm(f => ({ ...f, isSuperAdmin: e.target.checked }))} />
                  </label>
                  {form.phoneNumber.trim().length > 0 && (
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">شماره موبایل تایید شده</div>
                        <div className="edit-switch-desc">اگر فعال باشد، کاربر از تایید مجدد با OTP معاف می‌شود</div>
                      </div>
                      <Switch size="small" checked={form.phoneVerified} onChange={e => setForm(f => ({ ...f, phoneVerified: e.target.checked }))} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDialogOpen(false)}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSubmit}>
            {dialogMode === 'create' ? 'ایجاد ادمین' : 'ذخیره تغییرات'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="حذف کاربر"
        message={
          <span>
            آیا از حذف <strong>"{deleteUser?.name}"</strong> اطمینان دارید؟<br />این عملیات قابل بازگشت نیست.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف کاربر"
        confirmColor="error"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      {/* User Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth className="details-dialog">
        <div className="details-dialog-title">
          <span>جزئیات کاربر</span>
          <button className="details-close" onClick={() => setDetailsOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="details-dialog-content">
          {detailsUser && (
            <div className="details-content">
              {/* Profile Header */}
              <div className="det-profile">
                <div className="det-avatar">
                  {detailsUser.avatar ? <img src={detailsUser.avatar} alt={detailsUser.name} /> : <PersonIcon style={{ fontSize: 36, color: '#9ca3af' }} />}
                </div>
                <div className="det-profile-info">
                  <h3 className="det-name">{detailsUser.name}</h3>
                  <span className="det-email">{detailsUser.email}</span>
                  <div className="det-badges">
                    {detailsUser.isSuperAdmin && <span className="tag tag-sm tag-admin">مدیر ارشد</span>}
                    {detailsUser.isAdmin && !detailsUser.isSuperAdmin && <span className="tag tag-sm tag-admin">ادمین</span>}
                    <span className={`tag tag-sm ${detailsUser.isActive ? 'tag-active' : 'tag-inactive'}`}>
                      {detailsUser.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                    {detailsUser.isBanned && <span className="tag tag-sm tag-danger">مسدود</span>}
                  </div>
                </div>
              </div>

              {/* Info Rows */}
              <div className="det-section">
                <div className="det-row">
                  <span className="det-label">شماره موبایل</span>
                  <span className="det-val ltr">{detailsUser.phoneNumber || '-'}</span>
                </div>
                <div className="det-row">
                  <span className="det-label">آخرین ورود</span>
                  <span className="det-val">{formatDateTime(detailsUser.lastLogin)}</span>
                </div>
                <div className="det-row">
                  <span className="det-label">تاریخ ایجاد</span>
                  <span className="det-val">{formatDate(detailsUser.createdAt)}</span>
                </div>
                <div className="det-row">
                  <span className="det-label">آخرین بروزرسانی</span>
                  <span className="det-val">{formatDate(detailsUser.updatedAt)}</span>
                </div>
                {detailsUser.emailVerified !== undefined && (
                  <div className="det-row">
                    <span className="det-label">ایمیل تایید شده</span>
                    <span className={`tag tag-sm ${detailsUser.emailVerified ? 'tag-active' : 'tag-inactive'}`}>
                      {detailsUser.emailVerified ? 'بله' : 'خیر'}
                    </span>
                  </div>
                )}
                {detailsUser.phoneNumber && (
                  <div className="det-row">
                    <span className="det-label">موبایل تایید شده</span>
                    <span className={`tag tag-sm ${detailsUser.phoneVerified ? 'tag-active' : 'tag-inactive'}`}>
                      {detailsUser.phoneVerified ? 'بله' : 'خیر'}
                    </span>
                  </div>
                )}
                {detailsUser.isBanned && detailsUser.banReason && (
                  <div className="det-row">
                    <span className="det-label">دلیل مسدودی</span>
                    <span className="det-val">{detailsUser.banReason}</span>
                  </div>
                )}
              </div>

              {/* Roles */}
              {detailsUser.roles && detailsUser.roles.length > 0 && (
                <div className="det-section">
                  <div className="det-section-title">نقش‌ها</div>
                  <div className="det-roles">
                    {detailsUser.roles.map((r, i) => {
                      const roleName = r.roleName || roles.find(role => (role.id === r.roleId) || (role._id === r.roleId))?.name || r.roleId;
                      return <span key={i} className="tag tag-sm tag-role">{roleName}</span>;
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="det-actions">
                <UserLoginHistoryButton userId={getId(detailsUser)} userName={detailsUser.name} buttonLabel="تاریخچه ورود" size="small" variant="outlined" />
                <EntityHistoryButton entityType="User" entityId={getId(detailsUser)} buttonLabel="تاریخچه تغییرات" size="small" />
                {canEdit && (
                  <button className="btn btn-primary" onClick={() => { setDetailsOpen(false); openEdit(detailsUser); }}>
                    <EditIcon style={{ fontSize: 16 }} /> ویرایش
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.type}>{snackbar.msg}</Alert>
      </Snackbar>
    </div>
  );
};

export default UserManagement;
